import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  FileText,
  Users,
  Shield,
  BarChart3,
  Check,
} from "lucide-react";
import IndustriesWeServe from "@/components/ui/IndustriesWeServe";

const useCases = [
  {
    icon: FileText,
    category: "Procurement",
    title: "Vendor Quotation Standardization",
    description:
      "Normalize multi-vendor quotations into a single procurement-approved format for accurate comparison and faster approvals.",
    impacts: [
      "Reduced negotiation errors",
      "Faster approval cycles",
      "Controlled vendor intake",
    ],
    stat: "60%",
    statLabel: "Faster vendor comparison",
  },
  {
    icon: Users,
    category: "HR",
    title: "Resume Normalization",
    description:
      "Convert varied candidate resumes into structured, ATS-ready formats.",
    impacts: [
      "Faster screening",
      "Structured candidate comparison",
      "Reduced manual review effort",
    ],
    stat: "75%",
    statLabel: "Reduction in screening time",
  },
  {
    icon: Shield,
    category: "Compliance",
    title: "Regulatory Document Governance",
    description: "Standardize compliance forms before submission or archival.",
    impacts: [
      "Reduced compliance exposure",
      "Audit-ready formatting",
      "Controlled documentation standards",
    ],
    stat: "90%",
    statLabel: "Format consistency achieved",
  },
  {
    icon: BarChart3,
    category: "Operations",
    title: "Reporting Standardization",
    description: "Normalize multi-source reports into analytics-ready formats.",
    impacts: [
      "Reliable BI ingestion",
      "Reduced cleanup effort",
      "Faster reporting cycles",
    ],
    stat: "50%",
    statLabel: "Time saved in report prep",
  },
];

export const UseCases = () => {
  const industries = [
    {
      category: "Financial Services",
      title: "Automate Financial Workflows",
      description:
        "Automate document-heavy financial workflows with speed and accuracy.",
      points: [
        "Invoice and receipt processing",
        "Loan application data extraction",
        "KYC and identity verification",
        "Bank statement analysis",
      ],
      stat: "Faster Processing",
      statLabel: "Improved compliance and reduced manual effort",
    },
    {
      category: "Healthcare",
      title: "Streamline Medical Documentation",
      description:
        "Streamline medical documentation and administrative workflows.",
      points: [
        "Patient record digitization",
        "Insurance claim processing",
        "Medical form extraction",
        "Prescription data capture",
      ],
      stat: "Higher Efficiency",
      statLabel: "Maintained data sensitivity and accuracy",
    },
    {
      category: "Retail & E-commerce",
      title: "Scale Transactional Operations",
      description:
        "Simplify operations and manage large volumes of transactional data.",
      points: [
        "Order and invoice processing",
        "Vendor document automation",
        "Inventory data extraction",
        "Customer data processing",
      ],
      stat: "Scalable Ops",
      statLabel: "Grow without increasing manual effort",
    },
    {
      category: "Logistics & Supply Chain",
      title: "Optimize Supply Chain Documentation",
      description: "Handle complex documentation across the supply chain.",
      points: [
        "Bill of lading processing",
        "Shipment tracking documents",
        "Proof of delivery extraction",
        "Customs documentation",
      ],
      stat: "Better Visibility",
      statLabel: "Reduced delays and improved tracking",
    },
    {
      category: "Enterprise Operations",
      title: "Drive Internal Efficiency",
      description: "Optimize internal processes across departments.",
      points: [
        "HR document processing",
        "Contract analysis",
        "Procurement workflows",
        "Internal reporting automation",
      ],
      stat: "Team Productivity",
      statLabel: "Centralized automation across teams",
    },
    {
      category: "Legal & Compliance",
      title: "Process Legal Documents Accurately",
      description: "Process complex legal documents with precision.",
      points: [
        "Contract data extraction",
        "Compliance document review",
        "Case file organization",
        "Legal form processing",
      ],
      stat: "Reduced Review Time",
      statLabel: "Maintain accuracy with automation",
    },
  ];

  return (
    <div className="pt-16 md:pt-20">
      {/* HERO */}
      <section className="section-padding hero-texture min-h-[70vh] flex items-center ">
        <div className="container-width relative z-10 vertical-center">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* <p className="label-mono mb-6">Industry Applications</p> */}

            <h1 className="text-2xl md:text-5xl font-bold tracking-tight leading-[1.1] text-neutral-950 mb-8">
              Built for Every Industry That Runs on Documents & Data
            </h1>

            <p className="text-lg md:text-xl leading-relaxed text-neutral-600">
              Bridge Format AIQ transforms how organizations extract, process,
              and act on data — across industries, workflows, and use cases.
            </p>
            <Link to="/contact-us" className="btn-primary my-4 btn-hover-scale">
              Book a Demo
            </Link>
          </motion.div>
        </div>
      </section>
      {/* PLATFORM OVERVIEW */}
      <section className="section-padding bg-neutral-50">
        <div className="container-width">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* LABEL */}
            {/* <p className="label-mono mb-6">Platform Overview</p> */}

            {/* HEADING */}
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-neutral-950 mb-6">
              One Platform. Endless Possibilities.
            </h2>

            {/* CONTENT */}
            <p className="text-sm md:text-base text-neutral-600 leading-relaxed mb-4">
              Whether you're processing invoices, onboarding customers, or
              analyzing documents at scale, Bridge Format AIQ adapts to your
              workflows.
            </p>

            <p className="text-sm md:text-base text-neutral-600 leading-relaxed">
              Our AI-powered platform helps teams eliminate manual work, reduce
              errors, and unlock insights — no matter the industry.
            </p>
          </motion.div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="section-padding">
        <div className="container-width">
          {/* HEADER (CENTERED) */}
          <div className="max-w-3xl mx-auto text-center mb-20">
            {/* <p className="label-mono mb-6">Why Bridge Format AIQ</p> */}

            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-neutral-950 mb-6">
              Why Businesses Need Bridge Format AIQ?
            </h2>

            <p className="text-sm md:text-base text-neutral-600 leading-relaxed">
              Bridge Format AIQ helps organizations automate processing, reduce
              manual effort, and improve accuracy across key functions.
            </p>
          </div>

          {/* CONTENT */}
          <div className="space-y-28 md:space-y-32">
            {industries.map((item, index) => (
              <motion.div
                key={item.category}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index == industries.length - 1 ? "mb-0" : "mb-8 md:mb-12"}
                }`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                {/* LEFT */}
                <div className={index % 2 ? "lg:order-2" : ""}>
                  <p className="label-mono mb-4">{item.category}</p>

                  <h3 className="text-3xl md:text-5xl font-semibold tracking-tight text-neutral-950 mb-6">
                    {item.title}
                  </h3>

                  <p className="text-sm md:text-base text-neutral-600 leading-relaxed mb-8">
                    {item.description}
                  </p>

                  <ul className="space-y-3 mb-6">
                    {item.points.map((point) => (
                      <li key={point} className="flex items-center gap-3">
                        <Check className="w-4 h-4 text-primary" />
                        <span className="text-sm md:text-base text-neutral-600">
                          {point}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <p className="text-sm md:text-base text-neutral-600">
                    {item.statLabel}
                  </p>
                </div>

                {/* RIGHT CARD */}
                <div className={index % 2 ? "lg:order-1" : ""}>
                  <div className="bg-primary text-white p-10 rounded-xl">
                    {/* <p className="text-xs font-mono uppercase tracking-wider text-white/70 mb-4">
                      Business Impact
                    </p> */}

                    <div className="text-3xl md:text-5xl font-bold mb-3">
                      {item.stat}
                    </div>

                    <p className="text-sm md:text-base text-white/80">
                      {item.category}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <IndustriesWeServe />
      {/* <section className="section-padding bg-neutral-50">
        <div className="container-width text-center mb-16">
          <p className="label-mono mb-6">Expanding Applications</p>

          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-neutral-950">
            More Industries. Same Solution.
          </h2>
        </div>

        <div className="container-width grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            "Healthcare",
            "Legal",
            "Manufacturing",
            "Financial Services",
            "Logistics",
            "Government",
            "Education",
            "Real Estate",
          ].map((industry) => (
            <div
              key={industry}
              className="border border-neutral-200 bg-white p-6 text-center rounded-lg"
            >
              <span className="text-sm md:text-base font-medium text-neutral-950">
                {industry}
              </span>
            </div>
          ))}
        </div>
      </section> */}

      {/* CTA */}
      <section className="section-padding">
        <div className="container-width text-center max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-5xl font-semibold tracking-tight text-neutral-950 mb-6">
            See How Bridge Format AIQ Fits Your Industry
          </h2>

          {/* <p className="text-sm md:text-base text-neutral-600 mb-8">
            Upload your real documents and watch them transform into
            standardized formats.
          </p> */}

          <Link to="/contact-us" className="btn-primary gap-2">
            Book a Demo
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default UseCases;
